import type { RecipeStore } from '@platewell/core';
import type {
  BillingSummary,
  HouseholdRole,
  HouseholdSummary,
  SyncErrorPayload,
} from '@platewell/schema';
import type {
  ConnectionStatus,
  HouseholdManager,
  HouseholdSession,
  PhotoUploadQueue,
} from '@platewell/sync-client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { RecipeSearchIndex } from './search.js';

export interface PlatewellContextValue {
  manager: HouseholdManager;
  households: HouseholdSummary[];
  activeHouseholdId: string | null;
  setActiveHousehold: (householdId: string) => void;
  session: HouseholdSession | null;
  store: RecipeStore | null;
  search: RecipeSearchIndex | null;
  status: ConnectionStatus;
  /**
   * Whether this device has ever finished syncing the active household. Until it has, an
   * empty store is still loading rather than empty.
   */
  hasSynced: boolean;
  uploads: PhotoUploadQueue | null;
  role: HouseholdRole | null;
  /**
   * The last server-side rejection for the active household — quota, a lapsed
   * subscription, a rate limit. Cleared when the household changes.
   */
  syncError: SyncErrorPayload | null;
  clearSyncError: () => void;
  /** Billing detail for the active household. Owner-only, and null until fetched. */
  billing: BillingSummary | null;
  reloadBilling: () => Promise<void>;
  loading: boolean;
  error: Error | null;
  reloadHouseholds: () => Promise<void>;
}

const PlatewellContext = createContext<PlatewellContextValue | null>(null);

export interface PlatewellProviderProps {
  manager: HouseholdManager;
  uploads?: PhotoUploadQueue;
  apiBaseUrl?: string;
  /** Injectable for tests and for the mobile app, which sends a bearer token. */
  fetchHouseholds?: () => Promise<HouseholdSummary[]>;
  children: ReactNode;
}

/**
 * Owns the one piece of genuinely global client state: which household is open.
 *
 * Switching households tears down a store and stands up another, so it lives here rather
 * than in a route. The previous household's socket is dropped but its local data stays on
 * disk, which is what makes switching back instant and offline-safe.
 */
export function PlatewellProvider({
  manager,
  uploads,
  apiBaseUrl = '',
  fetchHouseholds,
  children,
}: PlatewellProviderProps) {
  const [households, setHouseholds] = useState<HouseholdSummary[]>([]);
  const [activeHouseholdId, setActiveHouseholdId] = useState<string | null>(() =>
    manager.getActiveHouseholdId(),
  );
  const [session, setSession] = useState<HouseholdSession | null>(null);
  const [search, setSearch] = useState<RecipeSearchIndex | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('offline');
  const [hasSynced, setHasSynced] = useState(
    () => activeHouseholdId != null && manager.hasSynced(activeHouseholdId),
  );
  const [syncError, setSyncError] = useState<SyncErrorPayload | null>(null);
  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadHouseholds = useCallback(async () => {
    const load =
      fetchHouseholds ??
      (async () => {
        const response = await fetch(`${apiBaseUrl}/api/households`, { credentials: 'include' });
        if (!response.ok) throw new Error(`failed to load households (${response.status})`);
        return ((await response.json()) as { households: HouseholdSummary[] }).households;
      });

    // A failed lookup means the network is gone, not that the user left every household, so
    // the last known list stands in. Without this a reload on a plane shows an error page in
    // front of data that is sitting right there on the device.
    let list: HouseholdSummary[];
    try {
      list = await load();
      manager.cacheHouseholds(list);
    } catch (cause) {
      const cached = manager.cachedHouseholds();
      if (cached == null) throw cause;
      list = cached as HouseholdSummary[];
    }

    setHouseholds(list);
    setActiveHouseholdId((current) => {
      // The stored choice can point at a household the user was removed from, or one that
      // was soft-deleted, so it is validated against the server list on every load.
      if (current != null && list.some((h) => h.id === current)) return current;
      return list[0]?.id ?? null;
    });
  }, [apiBaseUrl, fetchHouseholds, manager]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadHouseholds()
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause : new Error(String(cause)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadHouseholds]);

  useEffect(() => {
    if (activeHouseholdId == null) return;
    const household = households.find((h) => h.id === activeHouseholdId);
    if (household == null) return;

    let cancelled = false;
    let index: RecipeSearchIndex | null = null;

    manager.setActiveHouseholdId(activeHouseholdId);
    void manager
      .open(activeHouseholdId, household.role)
      .then((opened) => {
        if (cancelled) return;
        index = new RecipeSearchIndex(opened.store).start();
        setSession(opened);
        setSearch(index);
        setStatus(opened.connection.status());
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause : new Error(String(cause)));
      });

    return () => {
      cancelled = true;
      index?.destroy();
    };
  }, [activeHouseholdId, households, manager]);

  useEffect(
    () =>
      manager.onStatus((householdId, next) => {
        if (householdId === activeHouseholdId) setStatus(next);
      }),
    [manager, activeHouseholdId],
  );

  useEffect(() => {
    setHasSynced(activeHouseholdId != null && manager.hasSynced(activeHouseholdId));
    return manager.onFirstSync((householdId) => {
      if (householdId === activeHouseholdId) setHasSynced(true);
    });
  }, [manager, activeHouseholdId]);

  useEffect(() => {
    setSyncError(null);
    return manager.onServerError((householdId, next) => {
      if (householdId === activeHouseholdId) setSyncError(next);
    });
  }, [manager, activeHouseholdId]);

  const clearSyncError = useCallback(() => setSyncError(null), []);

  /**
   * Billing detail is server-only and owner-only, so unlike everything else here it is a
   * fetch rather than a document read, and simply unavailable offline. A non-owner or a
   * failed request leaves it null and the UI falls back to `HouseholdSummary.plan`.
   */
  const reloadBilling = useCallback(async () => {
    if (activeHouseholdId == null) {
      setBilling(null);
      return;
    }
    try {
      const response = await fetch(`${apiBaseUrl}/api/billing/${activeHouseholdId}`, {
        credentials: 'include',
      });
      setBilling(response.ok ? ((await response.json()) as BillingSummary) : null);
    } catch {
      setBilling(null);
    }
  }, [apiBaseUrl, activeHouseholdId]);

  useEffect(() => {
    setBilling(null);
  }, [activeHouseholdId]);

  const setActiveHousehold = useCallback(
    (householdId: string) => {
      manager.setActiveHouseholdId(householdId);
      setSession(null);
      setSearch(null);
      setActiveHouseholdId(householdId);
    },
    [manager],
  );

  const value = useMemo<PlatewellContextValue>(
    () => ({
      manager,
      households,
      activeHouseholdId,
      setActiveHousehold,
      session,
      store: session?.store ?? null,
      search,
      status,
      hasSynced,
      uploads: uploads ?? null,
      role: session?.role ?? null,
      syncError,
      clearSyncError,
      billing,
      reloadBilling,
      loading,
      error,
      reloadHouseholds: loadHouseholds,
    }),
    [
      manager,
      households,
      activeHouseholdId,
      setActiveHousehold,
      session,
      search,
      status,
      hasSynced,
      uploads,
      syncError,
      clearSyncError,
      billing,
      reloadBilling,
      loading,
      error,
      loadHouseholds,
    ],
  );

  return <PlatewellContext.Provider value={value}>{children}</PlatewellContext.Provider>;
}

export function usePlatewell(): PlatewellContextValue {
  const value = useContext(PlatewellContext);
  if (value == null) throw new Error('usePlatewell must be used inside a PlatewellProvider');
  return value;
}

/** Exported so the provider can be replaced wholesale in tests. */
export { PlatewellContext };
